import Model from '../../ModelBinding/Model';
import OrganizationCollection from '../Organization/OrganizationCollection';
import { can } from '../../Plugins/PermissionService';
import RoleCollection from '../Role/RoleCollection';
import EquipmentCollection from '../Equipment/EquipmentCollection';
import LanguageCollection from '../Language/LanguageCollection';
import InvitationCollection from '../Invitation/InvitationCollection';
import Profile from '../Profile/Profile';
import MemberCollection from './MemberCollection';
import dayjs from 'dayjs';
import PermissionCollection from '../Permission/PermissionCollection';
import Organization from '../Organization/Organization';
import ProductSubtypeCollection from '../ProductSubtype/ProductSubtypeCollection';
import CriticalityCollection from '../Criticality/CriticalityCollection';
import MissionCollection from '../Mission/MissionCollection';
import AddressCollection from '../Address/AddressCollection';

export default class Member extends Model {
  public short_id?: number;
  public pin_code?: string;
  public firstname?: string;
  public lastname?: string;
  public sex?: number;
  public missions_counter?: number;
  public email?: string;
  public email_verified_at?: dayjs.Dayjs;
  public phone_1?: string;
  public phone_2?: string;
  public phone_1_verified_at?: dayjs.Dayjs;
  public locale?: string;
  public position?: string;
  public enabled?: boolean;
  public created_at?: dayjs.Dayjs;
  public updated_at?: dayjs.Dayjs;
  public role?: string;
  public roles?: RoleCollection;
  public organizations?: OrganizationCollection;
  public equipment?: EquipmentCollection;
  public spoken_languages?: LanguageCollection;
  public invitations?: InvitationCollection;
  public profile?: Profile;
  public product_subtypes?: ProductSubtypeCollection;
  public equipment_customs?: any;
  public rate?: number;
  public criticalities?: CriticalityCollection;
  public missions_visibility?: boolean;
  public welcomed?: boolean;
  public application_done?: boolean;
  public last_rated_missions?: MissionCollection;
  public address?: AddressCollection;
  public last_mission?: dayjs.Dayjs;
  public last_login_at?: dayjs.Dayjs;
  public hubspot_id?: number;
  public missions_limitation?: number;
  public missions_attributed_today?: number;
  public payoneer_registered?: boolean;
  public payoneer_registration_link?: string;
  public payoneer_status?: string;
  public profile_picture?: any;
  public taken_appointment_slots?: Array<object>;
  public ongoing_missions_counter?: number;
  public ongoing_missions_limitation?: number;

  bind() {
    this.short_id = this.getIntAttribute('short_id');
    this.pin_code = this.getStringAttribute('pin_code');
    this.firstname = this.getStringAttribute('firstname');
    this.lastname = this.getStringAttribute('lastname');
    this.sex = this.getIntAttribute('sex');
    this.missions_counter = this.getIntAttribute('missions_counter');
    this.email = this.getStringAttribute('email');
    this.email_verified_at = this.getDateAttribute('email_verified_at');
    this.phone_1 = this.getStringAttribute('phone_1');
    this.phone_2 = this.getStringAttribute('phone_2');
    this.phone_1_verified_at = this.getDateAttribute('phone_1_verified_at');
    this.locale = this.getStringAttribute('locale');
    this.position = this.getStringAttribute('position');
    this.enabled = this.getBooleanAttribute('enabled');
    this.welcomed = this.getBooleanAttribute('welcomed');
    this.created_at = this.getDateAttribute('created_at');
    this.updated_at = this.getDateAttribute('updated_at');
    this.role = this.getStringAttribute('role');
    this.roles = this.getRelationCollection('roles', RoleCollection) as RoleCollection;
    this.organizations = this.getRelationCollection(
      'organizations',
      OrganizationCollection,
    ) as OrganizationCollection;
    this.equipment = this.getRelationCollection(
      'equipment',
      EquipmentCollection,
    ) as EquipmentCollection;
    this.spoken_languages = this.getRelationCollection(
      'spoken_languages',
      LanguageCollection,
    ) as LanguageCollection;
    this.invitations = this.getRelationCollection(
      'invitations',
      InvitationCollection,
    ) as InvitationCollection;
    this.profile = this.getRelation('profile', Profile);
    this.product_subtypes = this.getRelationCollection(
      'product_subtypes',
      ProductSubtypeCollection,
    ) as ProductSubtypeCollection;
    this.equipment_customs = this.getJsonRelationships()?.equipment_customs;
    this.rate = this.getFloatAttribute('rate');
    this.criticalities = this.getRelationCollection(
      'criticalities',
      CriticalityCollection,
    ) as CriticalityCollection;
    this.missions_visibility = this.getBooleanAttribute('missions_visibility');
    this.application_done = this.getBooleanAttribute('application_done');
    this.last_rated_missions = this.getRelationCollection(
      'last_rated_missions',
      MissionCollection,
    ) as MissionCollection;
    // @ts-ignore
    this.address = this.getRelationCollection('address', AddressCollection);
    this.last_mission = this.getDateAttribute('last_mission');
    this.last_login_at = this.getDateAttribute('last_login_at');
    this.hubspot_id = this.getIntAttribute('hubspot_id');
    this.missions_limitation = this.getIntAttribute('missions_limitation', 0);
    this.missions_attributed_today = this.getIntAttribute('missions_attributed_today', 0);
    this.payoneer_registered = this.getBooleanAttribute('payoneer_registered');
    this.payoneer_registration_link = this.getStringAttribute('payoneer_registration_link');
    this.payoneer_status = this.getStringAttribute('payoneer_status');
    this.profile_picture = this.getJsonRelationships()?.profile_picture;
    this.taken_appointment_slots = this.getOriginalAttribute('taken_appointment_slots');
    this.ongoing_missions_counter = this.getIntAttribute('ongoing_missions_counter', 0);
    this.ongoing_missions_limitation = this.getIntAttribute('ongoing_missions_limitation', 0);
  }

  static getCollection() {
    return MemberCollection;
  }

  hasRole(role: string) {
    return this.roles && this.roles.some(({ name }) => name === role);
  }

  can(permissions: string[], testType: 'and'|'or' = 'and') {
    return can(this, permissions, testType);
  }

  canAtLeast(permissions: string[]) {
    return can(this, permissions, 'or');
  }

  get fullname() {
    return `${this.firstname} ${this.lastname}`;
  }

  get permissions() {
    return this.roles ? this.roles.permissions : new PermissionCollection();
  }

  get currentOrganization(): Organization | undefined {
    if (!this.organizations || this.organizations.length === 0) return;

    let organizations = this.organizations.filter(organization => organization.role === 'owner');

    if (organizations.length === 0) {
      organizations = this.organizations;
    }

    const organizationWithAddresses = organizations.filter(
      organization => organization.addresses && organization.addresses.length > 0,
    );
    if (organizationWithAddresses.length > 0) {
      organizations = organizationWithAddresses;
    }

    const organization = organizations.find(organization => organization.ready);
    if (organization) return organization;

    return this.organizations.getItem(0);
  }

  getDashboard() {
    if (this.hasRole('operator')) {
      return 'operator';
    }
    if (this.hasRole('customer')) {
      return 'customer';
    }

    if (
      this.hasRole('GodMode') ||
      this.hasRole('admin') ||
      this.hasRole('staff') ||
      this.hasRole('editor') ||
      this.hasRole('hotliner')
    ) {
      return 'backoffice';
    }

    throw new Error('There is no dashboard compatible with your user account.');
  }

  static async getPersonalAccessTokens() {
    const client = this.QueryBuilder().getAxios();
    const { data } = await client.get('/oauth/personal-access-tokens');
    return data;
  }

  static async destroyPersonalAccessToken(id: string) {
    const client = this.QueryBuilder().getAxios();
    await client.delete(`/oauth/personal-access-tokens/${id}`);
  }

  static async createPersonalAccessToken(data: object) {
    const client = this.QueryBuilder().getAxios();
    return await client.post('/oauth/personal-access-tokens', data).then((token) => {
      return token.data;
    });
  }

  static fromOrganization(id: string) {
    return this.QueryBuilder(`organizations/${id}/relationships/members`);
  }
}
